import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Sparkles, Target, Briefcase, Layout, Users, Download,
	UserPlus, FileEdit, TrendingUp, ChevronDown, Check,
	Star, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram
} from 'lucide-react';
import Logo from '../components/Logo';
import FeatureCard from '../components/Landing/FeatureCard';
import PricingCard from '../components/Landing/PricingCard';
import TestimonialCard from '../components/Landing/TestimonialCard';

export default function Landing() {
	const navigate = useNavigate();
	const [activeAccordion, setActiveAccordion] = useState(null);

	const scrollToSection = (sectionId) => {
		const el = document.getElementById(sectionId);
		if (el) el.scrollIntoView({ behavior: 'smooth' });
	};

	// Reveal on scroll
	useEffect(() => {
		const elements = document.querySelectorAll('.reveal-on-scroll');
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add('opacity-100', 'translate-y-0');
						observer.unobserve(entry.target);
					}
				});
			},
			{ threshold: 0.15 }
		);
		elements.forEach((el) => observer.observe(el));
		return () => observer.disconnect();
	}, []);

	// Count up hook
	const useCountUp = (target, startWhenVisible = true, durationMs = 1200) => {
		const [value, setValue] = useState(0);
		const ref = useRef(null);
		useEffect(() => {
			if (!startWhenVisible) return;
			let started = false;
			const observer = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && !started) {
					started = true;
					const start = performance.now();
					const step = (now) => {
						const progress = Math.min(1, (now - start) / durationMs);
						setValue(Math.floor(progress * target));
						if (progress < 1) requestAnimationFrame(step);
					};
					requestAnimationFrame(step);
				}
			}, { threshold: 0.2 });
			if (ref.current) observer.observe(ref.current);
			return () => observer.disconnect();
		}, [target, startWhenVisible, durationMs]);
		return { value, ref };
	};

	// Call hooks at the top level, not inside useMemo
	const resumesCount = useCountUp(10000);
	const atsPassCount = useCountUp(95);
	const matchesCount = useCountUp(500);
	const ratingCount = useCountUp(49); // will display 4.9★

	const stats = useMemo(() => ({
		resumes: resumesCount,
		atsPass: atsPassCount,
		matches: matchesCount,
		rating: ratingCount
	}), [resumesCount, atsPassCount, matchesCount, ratingCount]);

	return (
		<div className="min-h-screen bg-white">
			{/* Hero Section */}
			<section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 via-blue-700 to-purple-600 text-white overflow-hidden">
				<div className="absolute inset-0 opacity-20"
					style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.2), transparent 25%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.15), transparent 25%)' }}
				/>
				<div className="relative z-10 max-w-6xl mx-auto px-6 py-24 text-center">
					<Logo size="xl" className="mx-auto mb-4" />
					<h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
						Build ATS-Optimized Resumes with AI in Minutes
					</h1>
					<p className="mt-4 text-lg md:text-xl text-blue-100 max-w-3xl mx-auto">
						Stand out from the crowd with AI-powered resume building, real-time ATS analysis, and smart job matching.
					</p>
					<div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
						<button
							onClick={() => navigate('/register')}
							className="px-8 py-4 rounded-xl bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors shadow-lg"
						>
							Get Started Free
						</button>
						<button
							onClick={() => scrollToSection('features')}
							className="px-8 py-4 rounded-xl border border-blue-200/50 text-white hover:bg-white/10 transition-colors"
						>
							Watch Demo
						</button>
					</div>

					{/* Floating cards */}
					<div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4">
						<div className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-sm">
							<div className="flex items-center gap-2">
								<Sparkles className="w-5 h-5 text-yellow-300" /> <span className="font-semibold">AI Suggestions</span>
							</div>
							<p className="text-sm text-blue-100 mt-1">Generate strong bullet points instantly.</p>
						</div>
						<div className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-sm">
							<div className="flex items-center gap-2">
								<Target className="w-5 h-5 text-green-300" /> <span className="font-semibold">ATS Insights</span>
							</div>
							<p className="text-sm text-blue-100 mt-1">Improve score with actionable tips.</p>
						</div>
						<div className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-sm">
							<div className="flex items-center gap-2">
								<Briefcase className="w-5 h-5 text-purple-200" /> <span className="font-semibold">Job Matching</span>
							</div>
							<p className="text-sm text-blue-100 mt-1">Find roles aligned with your skills.</p>
						</div>
					</div>

					{/* Scroll down indicator */}
					<button
						onClick={() => scrollToSection('trust')}
						className="mt-14 inline-flex items-center gap-2 text-blue-100 hover:text-white transition-colors"
						aria-label="Scroll to next section"
					>
						<ChevronDown className="w-6 h-6 animate-bounce" />
						<span>Scroll</span>
					</button>
				</div>
			</section>

			{/* Trust Indicators */}
			<section id="trust" className="py-10 bg-gray-50">
				<div className="max-w-6xl mx-auto px-6">
					<div className="flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
						<div className="reveal-on-scroll opacity-0 translate-y-4 transition-all duration-700">
							<p className="text-gray-900 font-semibold">Trusted by 10,000+ job seekers</p>
						</div>
						<div className="reveal-on-scroll opacity-0 translate-y-4 transition-all duration-700 delay-100 flex items-center gap-1">
							{Array.from({ length: 5 }).map((_, i) => (<Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />))}
							<span className="ml-2 text-gray-700">4.9/5 average rating</span>
						</div>
						<div className="reveal-on-scroll opacity-0 translate-y-4 transition-all duration-700 delay-200">
							<p className="text-gray-700">500+ resumes created daily</p>
						</div>
						<div className="reveal-on-scroll opacity-0 translate-y-4 transition-all duration-700 delay-300">
							<p className="text-gray-700">95% ATS pass rate</p>
						</div>
					</div>
				</div>
			</section>

			{/* Features Section */}
			<section id="features" className="py-20 px-6">
				<div className="max-w-6xl mx-auto">
					<h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center">Everything You Need to Land Your Dream Job</h2>
					<p className="text-gray-600 text-center max-w-2xl mx-auto mt-3">Powerful features to create, optimize, and manage your resumes</p>
					<div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
						<FeatureCard icon={Sparkles} title="AI-Powered Resume Builder" description="Create professional resumes with AI-generated content suggestions." />
						<FeatureCard icon={Target} title="ATS Score Analyzer" description="Get instant feedback on how your resume performs with ATS systems." />
						<FeatureCard icon={Briefcase} title="Smart Job Matching" description="Find jobs that match your skills with personalized recommendations." />
						<FeatureCard icon={Layout} title="Multiple Templates" description="Choose from modern, professional, creative, and minimal templates." />
						<FeatureCard icon={Users} title="Real-time Collaboration" description="Share resumes with recruiters via unique links." />
						<FeatureCard icon={Download} title="Export Anywhere" description="Download your resume as PDF or DOCX with one click." />
					</div>
				</div>
			</section>

			{/* How It Works */}
			<section id="how-it-works" className="py-20 px-6 bg-gray-50">
				<div className="max-w-6xl mx-auto">
					<h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center">Get Started in 3 Simple Steps</h2>
					<div className="mt-12 grid gap-6 md:grid-cols-3">
						<StepCard
							icon={UserPlus}
							title="Create Your Account"
							description="Sign up in seconds and get 5 free credits daily."
						/>
					                <StepConnector />
						<StepCard
							icon={FileEdit}
							title="Build Your Resume"
							description="Use our AI-powered builder with smart suggestions."
						/>
					                <StepConnector />
						<StepCard
							icon={TrendingUp}
							title="Optimize & Apply"
							description="Analyze ATS score, match jobs, and start applying."
						/>
					</div>
				</div>
			</section>

			{/* ATS Analysis Demo */}
			<section className="py-20 px-6">
				<div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
					<div className="reveal-on-scroll opacity-0 translate-y-4 transition-all duration-700">
						<h3 className="text-2xl md:text-3xl font-extrabold text-gray-900">See Your ATS Score Instantly</h3>
						<p className="text-gray-600 mt-2">Know exactly how your resume performs before applying.</p>
						<div className="mt-6 grid sm:grid-cols-2 gap-4">
							<div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
								<p className="text-sm text-gray-500 mb-2">Before</p>
								<div className="h-36 bg-gray-100 rounded-lg" />
							</div>
							<div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm ring-2 ring-green-200">
								<p className="text-sm text-gray-500 mb-2">After</p>
								<div className="h-36 bg-green-50 rounded-lg" />
							</div>
						</div>
					</div>
					<div className="reveal-on-scroll opacity-0 translate-y-4 transition-all duration-700 delay-150">
						<div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
							<div className="flex items-center justify-between">
								<p className="text-gray-700 font-semibold">ATS Score</p>
								<span className="text-sm text-gray-500">Sample</span>
							</div>
							<div className="mt-4 flex items-center gap-6">
								<div className="relative">
									<div className="w-28 h-28 rounded-full bg-blue-50 flex items-center justify-center">
										<span className="text-2xl font-extrabold text-blue-600">87</span>
									</div>
								</div>
								<div className="space-y-3">
									<div className="flex items-center gap-2">
										<Check className="w-5 h-5 text-green-500" /> <span className="text-gray-700 font-medium">Keywords Found:</span> <span className="text-gray-900 font-semibold">15</span>
									</div>
									<div className="flex items-center gap-2">
										<Check className="w-5 h-5 text-green-500" /> <span className="text-gray-700 font-medium">Format Issues:</span> <span className="text-gray-900 font-semibold">2</span>
									</div>
									<div className="flex items-center gap-2">
										<Check className="w-5 h-5 text-green-500" /> <span className="text-gray-700 font-medium">Score Improvement:</span> <span className="text-gray-900 font-semibold">+42</span>
									</div>
								</div>
							</div>
							<button
								onClick={() => navigate('/ats-analyzer')}
								className="mt-6 w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
							>
								Analyze My Resume
							</button>
						</div>
					</div>
				</div>
			</section>

			{/* Pricing / Credits */}
			<section id="pricing" className="py-20 px-6 bg-gray-50">
				<div className="max-w-6xl mx-auto">
					<h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center">Flexible Credit System</h2>
					<p className="text-gray-600 text-center max-w-2xl mx-auto mt-3">Pay only for what you need, when you need it</p>
					<div className="mt-12 grid gap-6 md:grid-cols-3">
						<PricingCard
							title="Starter"
							price={10}
							credits={20}
							perCredit={0.5}
							features={['AI suggestions', 'ATS analysis', 'Job matching', 'Email support']}
							onSelect={() => navigate('/credits')}
						/>
						<PricingCard
							title="Popular"
							price={25}
							credits={50}
							perCredit={0.5}
							popular
							features={['Everything in Starter', 'Priority support', 'Advanced ATS checks']}
							onSelect={() => navigate('/credits')}
						/>
						<PricingCard
							title="Pro"
							price={50}
							credits={120}
							perCredit={0.42}
							badgeText="Best Value"
							features={['Everything in Popular', 'Export DOCX/PDF', 'Collaboration links']}
							onSelect={() => navigate('/credits')}
						/>
					</div>
					<div className="mt-8 bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm">
						<p className="font-semibold text-gray-900">Get 5 FREE credits daily</p>
						<p className="text-gray-600 text-sm">No credit card required</p>
					</div>
				</div>
			</section>

			{/* Testimonials */}
			<section className="py-20 px-6">
				<div className="max-w-6xl mx-auto">
					<h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center">Loved by Job Seekers Worldwide</h2>
					<div className="mt-12 grid gap-6 md:grid-cols-3">
						<TestimonialCard
							name="Priya Sharma"
							role="Software Engineer"
							quote="Resumely helped me land my dream job! The ATS analyzer was a game-changer."
							rating={5}
						/>
						<TestimonialCard
							name="Rahul Verma"
							role="Product Manager"
							quote="The AI suggestions made my resume 10x better. Highly recommended!"
							rating={5}
						/>
						<TestimonialCard
							name="Anjali Gupta"
							role="Designer"
							quote="Best resume builder I've used. The templates are modern and professional."
							rating={5}
						/>
					</div>
				</div>
			</section>

			{/* Stats Section */}
			<section className="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
				<div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
					<div ref={stats.resumes.ref}>
						<p className="text-5xl font-extrabold">{stats.resumes.value.toLocaleString()}+</p>
						<p className="mt-2 text-blue-100">Resumes Created</p>
					</div>
					<div ref={stats.atsPass.ref}>
						<p className="text-5xl font-extrabold">{stats.atsPass.value}%</p>
						<p className="mt-2 text-blue-100">ATS Pass Rate</p>
					</div>
					<div ref={stats.matches.ref}>
						<p className="text-5xl font-extrabold">{stats.matches.value}+</p>
						<p className="mt-2 text-blue-100">Job Matches Daily</p>
					</div>
					<div ref={stats.rating.ref}>
						<p className="text-5xl font-extrabold">4.{Math.min(9, Math.floor(stats.rating.value / 10))}★</p>
						<p className="mt-2 text-blue-100">Average Rating</p>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section id="faq" className="py-20 px-6">
				<div className="max-w-4xl mx-auto">
					<h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center">Frequently Asked Questions</h2>
					<div className="mt-10 space-y-3">
						{faqItems.map((item, idx) => (
							<div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
								<button
									className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100"
									onClick={() => setActiveAccordion(activeAccordion === idx ? null : idx)}
									aria-expanded={activeAccordion === idx}
									aria-controls={`faq-panel-${idx}`}
								>
									<span className="font-semibold text-gray-900">{item.q}</span>
									<ChevronDown className={`w-5 h-5 transition-transform ${activeAccordion === idx ? 'rotate-180' : ''}`} />
								</button>
								<div
									id={`faq-panel-${idx}`}
									className={`px-5 transition-all overflow-hidden ${activeAccordion === idx ? 'py-4 max-h-96' : 'max-h-0'}`}
								>
									<p className="text-gray-700">{item.a}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Final CTA */}
			<section className="py-20 px-6 bg-gradient-to-br from-blue-600 to-purple-600 text-white text-center">
				<h2 className="text-3xl md:text-4xl font-extrabold">Ready to Build Your Perfect Resume?</h2>
				<p className="mt-2 text-blue-100">Join thousands of job seekers who landed their dream jobs</p>
				<button
					onClick={() => navigate('/register')}
					className="mt-6 px-8 py-4 rounded-xl bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors shadow-lg"
				>
					Start Building Now - It's Free
				</button>
				<p className="mt-3 text-sm text-blue-100">No credit card required • 5 free credits daily</p>
				<div className="mt-6 flex items-center justify-center gap-4 text-blue-100 text-sm">
					<span className="px-3 py-1 rounded-full border border-white/30">Secure</span>
					<span className="px-3 py-1 rounded-full border border-white/30">Privacy Protected</span>
					<span className="px-3 py-1 rounded-full border border-white/30">Razorpay Payments</span>
				</div>
			</section>

			{/* Footer */}
			<footer className="bg-gray-900 text-gray-300 pt-14 pb-8 px-6">
				<div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-10">
					<div>
						<Logo size="md" className="mb-2" />
						<p className="text-gray-400">AI Resume Builder • ATS Analyzer • Job Matcher</p>
					<div className="mt-4 flex items-center gap-3">
						<a href="https://linkedin.com" className="hover:text-white" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
							<Linkedin className="w-5 h-5" />
						</a>
						<a href="https://twitter.com" className="hover:text-white" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
							<Twitter className="w-5 h-5" />
						</a>
						<a href="https://facebook.com" className="hover:text-white" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
							<Facebook className="w-5 h-5" />
						</a>
						<a href="https://instagram.com" className="hover:text-white" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
							<Instagram className="w-5 h-5" />
						</a>
					</div>
					</div>
					<div>
					<h4 className="text-white font-semibold mb-3">Product</h4>
					<ul className="space-y-2">
						<li><button className="hover:text-white text-left" onClick={() => navigate('/resumes')}>Resume Builder</button></li>
						<li><button className="hover:text-white text-left" onClick={() => navigate('/ats-analyzer')}>ATS Analyzer</button></li>
						<li><button className="hover:text-white text-left" onClick={() => navigate('/jobs')}>Job Matcher</button></li>
						<li><a className="hover:text-white" href="#features">Templates</a></li>
					</ul>
					</div>
					<div>
					<h4 className="text-white font-semibold mb-3">Resources</h4>
					<ul className="space-y-2">
						<li><a className="hover:text-white" href="/blog">Blog</a></li>
						<li><a className="hover:text-white" href="/help">Help Center</a></li>
						<li><a className="hover:text-white" href="/tips">Resume Tips</a></li>
						<li><a className="hover:text-white" href="/advice">Career Advice</a></li>
					</ul>
					</div>
					<div>
					<h4 className="text-white font-semibold mb-3">Company</h4>
					<ul className="space-y-2">
						<li><a className="hover:text-white" href="/about">About Us</a></li>
						<li><a className="hover:text-white" href="/contact">Contact</a></li>
						<li><a className="hover:text-white" href="/privacy">Privacy Policy</a></li>
						<li><a className="hover:text-white" href="/terms">Terms of Service</a></li>
					</ul>
					</div>
					<div>
						<h4 className="text-white font-semibold mb-3">Contact</h4>
						<ul className="space-y-2">
							<li className="flex items-center gap-2"><Mail className="w-4 h-4" /> support@resumely.com</li>
							<li className="flex items-center gap-2"><Phone className="w-4 h-4" /> +91 98765 43210</li>
							<li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Mumbai, India</li>
						</ul>
					</div>
				</div>
				<div className="mt-10 pt-6 border-t border-gray-800 text-sm text-gray-400 max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
					<p>© 2024 Resumely. All rights reserved.</p>
					<p>Payment Partners: Razorpay</p>
				</div>
			</footer>
		</div>
	);
}

function StepCard({ icon: Icon, title, description }) {
	return (
		<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-center reveal-on-scroll opacity-0 translate-y-4 transition-all duration-700">
			<div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mx-auto">
				<Icon className="w-6 h-6" />
			</div>
			<h3 className="mt-4 font-bold text-gray-900 text-lg">{title}</h3>
			<p className="text-gray-600 mt-1">{description}</p>
		</div>
	);
}

function StepConnector() {
	return <div className="hidden md:flex items-center justify-center">
		<div className="h-1 w-12 bg-gray-200 rounded-full" />
	</div>;
}

const faqItems = [
	{ q: 'What is an ATS and why does it matter?', a: 'An Applicant Tracking System (ATS) scans resumes for keywords and formatting. Resumely helps you optimize your resume to pass ATS checks so you get seen by recruiters.' },
	{ q: 'How do credits work?', a: 'Credits are used for AI suggestions, ATS analysis, and job matching. You receive 5 free credits daily and can purchase additional credits anytime.' },
	{ q: 'Can I export my resume for free?', a: 'Yes, you can export to PDF. DOCX export may require credits depending on your plan.' },
	{ q: 'Do you offer refunds?', a: 'If you face any issues, contact support within 7 days and we will try our best to help. Certain credit purchases may be refundable as per policy.' },
	{ q: 'Is my data secure?', a: 'We use industry best practices including encryption in transit and secure storage. You control what you share.' },
	{ q: 'What file formats are supported?', a: 'You can import PDF and DOCX for analysis. Exports are available in PDF and DOCX.' }
];

